import { CheckOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

import { Badge } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import type { PermTable } from '../../types';

/**
 * Một hàng bảng: bật/tắt đọc, và khi bật thì hiện cột dạng nút.
 *
 * Nút thay checkbox lồng: mỗi cột nói rõ trạng thái của CHÍNH NÓ (mắt mở = đọc được, mắt gạch = đang ẩn), thay vì một ô
 * tích mà nghĩa "tích là ẩn" phải đọc dòng hướng dẫn mới biết.
 */
export function HangBang({
  table, denied, on, onToggle, onCol, readOnly,
}: {
  table: PermTable;
  denied?: string[];
  on: boolean;
  onToggle: (on: boolean) => void;
  onCol: (col: string, cam: boolean) => void;
  readOnly: boolean;
}) {
  const { t, locale } = useT();
  const cam = new Set(denied ?? []);
  return (
    <div className={cn('rounded border border-default transition', on ? 'bg-surface' : 'bg-subtle')}>
      <button
        type="button"
        disabled={readOnly}
        aria-pressed={on}
        onClick={() => onToggle(!on)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-start disabled:cursor-not-allowed"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className={cn(
              'flex size-4 shrink-0 items-center justify-center rounded-sm transition',
              on ? 'bg-primary-600 text-content-inverse' : 'ring-1 ring-inset ring-strong',
            )}
          >
            {on ? <CheckOutlined className="text-2xs" /> : null}
          </span>
          <span className="min-w-0">
            <span className="text-xs font-medium text-content-primary">{table.title}</span>
            <span className="ms-1.5 break-all font-mono text-xs text-content-muted">{table.name}</span>
          </span>
        </span>
        {on && cam.size ? (
          <Badge variant="warning">{t('troLyAiBotQuyenDuLieu.giauCot', { n: soVN(cam.size, locale) })}</Badge>
        ) : (
          <span className="shrink-0 text-xs text-content-muted">
            {on ? t('troLyAiBotQuyenDuLieu.soCot', { n: soVN(table.columns.length, locale) }) : ''}
          </span>
        )}
      </button>

      {on ? (
        <div className="border-t border-default px-3 pb-3 pt-2.5">
          <p className="mb-2 text-xs text-content-muted">
            {t('troLyAiBotQuyenDuLieu.huongDanCotTruoc')}{' '}
            <b className="text-warning-700">{t('troLyAiBotQuyenDuLieu.huongDanCotDam')}</b>{' '}
            {t('troLyAiBotQuyenDuLieu.huongDanCotSau')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {table.columns.map((c) => {
              const an = cam.has(c.name);
              return (
                <button
                  key={c.name}
                  type="button"
                  disabled={readOnly}
                  aria-pressed={an}
                  onClick={() => onCol(c.name, !an)}
                  title={an ? t('troLyAiBotQuyenDuLieu.dangGiau') : t('troLyAiBotQuyenDuLieu.dangHien')}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded px-2 py-1 font-mono text-xs transition disabled:cursor-not-allowed',
                    an
                      ? 'bg-warning-50 text-warning-700 ring-1 ring-warning-500'
                      : 'bg-subtle text-content-secondary hover:text-content-primary',
                  )}
                >
                  {an ? <EyeInvisibleOutlined className="text-xs" /> : <EyeOutlined className="text-xs text-content-muted" />}
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
